"use client";

import React from "react";
import { Star, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  id: "srv-1",
  title: "Single Area Precision",
  description: "Target specific concerns such as forehead lines, frown lines, or crow's feet for a refreshed and subtle improvement.",
  price: "£145",
  duration: "30 min",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603357/img/anti_wrinkle_area_1.png",
  badge: "Refined"
}, {
  id: "srv-2",
  title: "Dual Area Harmony",
  description: "Address two key areas to achieve a more balanced facial rejuvenation and a smoother, more youthful profile.",
  price: "£175",
  duration: "45 min",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603357/img/anti_wrinkle_area_2.png",
  badge: "Popular"
}, {
  id: "srv-3",
  title: "Full Three-Area Ritual",
  description: "Our comprehensive upper face treatment covering forehead, frown, and eyes for a complete, graceful transformation.",
  price: "£205",
  duration: "60 min",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603357/img/anti_wrinkle_full.png",
  badge: "Signature"
}];
const testimonials = [{
  id: "tst-1",
  quote: "being an older lady having lip filler I was a little apprehensive they wouldn't look natural. I didn't need to worry. very pleased 👍",
  name: "Beverley Guyton Richards",
  role: "Local Resident",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603357/img/client_helena.png",
  initials: "HR"
}, {
  id: "tst-2",
  quote: "I had lip filler done by Natalia. I 100% recommend. She provided a friendly, warm service, and I am over the moon with my lips. They look fantastic!!\nAmazing results",
  name: "Jasmin Nash",
  role: "Business Owner",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603357/img/client_james.png",
  initials: "JM"
}, {
  id: "tst-3",
  quote: "I've visited many clinics in Norwich, but Natalia's attention to detail and the serene environment make it a truly five-star experience. My forehead has never looked smoother or more natural.",
  name: "Sophie Laurent",
  role: "Marketing Director",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603357/img/client_sophie.png",
  initials: "SL"
}];
export default function PremiumWellness() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: servicesRef,
    isVisible: areServicesVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: testimonialsRef,
    isVisible: areTestimonialsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="174665" id="antiwrinkleinjections" className="w-full bg-background pt-24 pb-16 overflow-hidden">
      {}
      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerRef} className={`flex flex-col items-center text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <Badge variant="secondary" className="mb-4 bg-primary/20 text-foreground hover:bg-primary/30 px-3 py-1 text-sm rounded-lg font-sans tracking-widest uppercase">
            <Sparkles className="w-4 h-4 mr-2" />
            Aesthetic Excellence
          </Badge>
          <h2 className="text-3xl md:text-6xl font-serif font-medium tracking-tight text-foreground mb-4">
            Anti-Wrinkle Injections
          </h2>
          <p className="text-lg font-sans text-muted-foreground leading-relaxed">
            Discover precision aesthetics in Brooke, Norwich. Our curated treatments are designed to soften lines while preserving your natural expression and facial character.
          </p>
        </div>

        <div ref={servicesRef} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 transition-all duration-1000 delay-200 ${areServicesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {services.map((service, i) => <div data-index={i} key={service.id} className="h-full">
              <Card className="h-full flex flex-col rounded-xl overflow-hidden border-border/40 bg-card/50 backdrop-blur-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-primary/5 hover:border-primary/20 group">
                <div className="relative h-64 overflow-hidden">
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-background/80 text-foreground backdrop-blur-md border-none shadow-sm font-sans text-xs">
                      {service.badge}
                    </Badge>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-background/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start mb-2">
                    <CardTitle className="text-2xl font-serif font-medium group-hover:text-foreground/80 transition-colors">
                      {service.title}
                    </CardTitle>
                    <span className="font-sans font-semibold text-lg text-foreground/70">{service.price}</span>
                  </div>
                  <CardDescription className="text-sm font-sans text-muted-foreground line-clamp-2 leading-relaxed">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="flex-grow">
                  <div className="flex items-center text-xs font-sans text-muted-foreground font-medium uppercase tracking-widest">
                    <span className="inline-block w-1.5 h-1.5 rounded-lg bg-primary/60 mr-2" />
                    {service.duration} Appointment
                  </div>
                </CardContent>
                
                <CardFooter className="pt-4 border-t border-border/20">
                  <Button asChild className="w-full rounded-lg group/btn bg-primary text-primary-foreground hover:bg-primary/90 transition-all">
                    <Link to={i === 2 ? "tel:07766733976" : "tel:07766733976"}>
                      Book Consultation
                      <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover/btn:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>

      {}
      <div className="mt-32 relative">
        {}
        <div className="absolute inset-0 bg-gradient-to-b from-background via-accent/40 to-background pointer-events-none" />
        
        <div className="container mx-auto px-4 md:px-6 relative z-10 py-20">
          <div ref={testimonialsRef} className={`transition-all duration-700 ${areTestimonialsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <h3 className="text-3xl md:text-5xl font-serif font-medium tracking-tight text-foreground mb-4">
                Client Experiences
              </h3>
              <p className="text-muted-foreground font-sans text-lg">
                Discover why our clients trust Natalia's Beauty for their aesthetic journey in Norfolk.
              </p>
            </div>

            <div className="max-w-5xl mx-auto px-4 sm:px-12">
              <Carousel opts={{
              align: "start",
              loop: true
            }} className="w-full">
                <CarouselContent className="-ml-4 md:-ml-6">
                  {testimonials.map((testimonial, i) => <CarouselItem data-index={i} key={testimonial.id} className="pl-4 md:pl-6 md:basis-1/2 lg:basis-1/2">
                      <div className="h-full p-1">
                        <Card className="h-full rounded-2xl border-none shadow-xl shadow-foreground/5 bg-background/60 backdrop-blur-md hover:shadow-2xl transition-shadow duration-500">
                          <CardContent className="p-8 flex flex-col h-full justify-between">
                            <div>
                              <div className="flex gap-1 mb-6">
                                {[1, 2, 3, 4, 5].map(star => <Star key={star} className="w-4 h-4 fill-primary text-primary opacity-60" />)}
                              </div>
                              <p className="text-lg font-serif text-foreground/90 leading-relaxed italic mb-8">
                                "{testimonial.quote}"
                              </p>
                            </div>
                            <div className="flex items-center gap-4 mt-auto">
                              
                              <div>
                                <h4 className="font-sans font-semibold text-foreground">{testimonial.name}</h4>
                                
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    </CarouselItem>)}
                </CarouselContent>
                <div className="hidden sm:block">
                  <CarouselPrevious className="bg-background/80 border-border/40 hover:bg-primary hover:text-primary-foreground transition-colors" />
                  <CarouselNext className="bg-background/80 border-border/40 hover:bg-primary hover:text-primary-foreground transition-colors" />
                </div>
              </Carousel>
            </div>
          </div>
        </div>
      </div>
    </section>;
}